import "src/style/components/FinishedComponent.scss";

import { Button, Typography, useTheme } from "@mui/material";
import { ReactElement, useEffect, useState } from "react";
import { useAppSelector } from "src/hooks/useAppSelector";
import { ProgressStage } from "src/types";
import { decimalHourToString, getTimeLogTotalHours } from "src/utils/misc";
import { useDispatch } from "react-redux";
import { setProgressStage } from "src/store/reducers/appSlice";

interface IFinishedComponentProps {
    resetHandler: () => void;
}

export default function FinishedComponent({
    resetHandler,
}: IFinishedComponentProps): ReactElement {
    const dispatch = useDispatch();
    const theme = useTheme();

    const timeLogs = useAppSelector((state) => state.app.timeLogs);
    const progressStage = useAppSelector((state) => state.app.progressStage);
    const timesheetId = useAppSelector((state) => state.app.timesheetId);

    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (progressStage === ProgressStage.SUBMITTED) {
            setSubmitted(true);
        }
        dispatch(setProgressStage(ProgressStage.FINISHED));
    }, []);

    return (
        <div className="finished">
            <div className="finished__content fill-expand">
                {" "}
                <div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="80"
                        height="80"
                        viewBox="0 0 105.54 122.88"
                    >
                        <path
                            fill={theme.palette.success.main}
                            d="M77.88,67.55a27.66,27.66,0,1,1-19.57,8.1,27.62,27.62,0,0,1,19.57-8.1Zm10.9,34.39a3.07,3.07,0,0,0,.53-.07,2.16,2.16,0,0,0,.51-.16,3,3,0,0,0,1.35-1.07A3,3,0,0,0,91.73,99v-.21a3.07,3.07,0,0,0-2.32-2.91,3.81,3.81,0,0,0-.59-.08h0c-1.16,0-7.24-.1-8.49-.17l-1.07-.06.34-10.29a3.92,3.92,0,0,0,0-.59,3.58,3.58,0,0,0-.16-.56,2.94,2.94,0,0,0-.27-.53,3.32,3.32,0,0,0-.37-.47h0a4.11,4.11,0,0,0-.45-.38,2.59,2.59,0,0,0-.52-.29,3.69,3.69,0,0,0-.56-.19,3.42,3.42,0,0,0-.57-.07h-.1a3,3,0,0,0-1.13.21,3.26,3.26,0,0,0-1,.63l-.07.07a4.62,4.62,0,0,0-.34.41,4.91,4.91,0,0,0-.28.5,3.08,3.08,0,0,0-.25,1.14L73,98.41a3.81,3.81,0,0,0,0,.59,2.84,2.84,0,0,0,.16.57,2.37,2.37,0,0,0,.26.53,2.73,2.73,0,0,0,.35.47l0,0a2.3,2.3,0,0,0,.41.36l0,0a4.39,4.39,0,0,0,.47.27,3.27,3.27,0,0,0,.55.2h0a3.65,3.65,0,0,0,.55.08h0c.94,0,1.92.11,2.92.18,1.72.12,8.3.24,9.88.2Zm.69-44.45V61A36.11,36.11,0,0,0,83,59.49v-2h0V36.17H55.91a3.27,3.27,0,0,1-3.27-3.27V6.48H6.52V105H43.13l0,.09v0l0,.1,0,.06v.08l0,.08h0l0,.09h0l0,.08v0l0,.06v.09l0,.06v0l0,.07h0l0,.09h0l0,.08v0l0,.06v.09l.06.17h0l0,.09h0l0,.07v0l0,0,.08.26h0l0,.08v0l0,.07,0,0v0l0,.06v0l0,.07h0l.07.18v0l0,.06v0l0,.1v0l.06.17h0l0,.07c.12.31.24.62.37.93h0l.17.4h0l.17.4h0l.22.49h0c.18.4.37.79.56,1.18H5.83a5.86,5.86,0,0,1-4.12-1.71A5.76,5.76,0,0,1,0,105.66V5.83A5.84,5.84,0,0,1,5.83,0H55.91a3.22,3.22,0,0,1,2.51,1.19l28.7,28a3.24,3.24,0,0,1,2.36,3.13V57.49ZM59.16,11.4,78.64,29.66H59.16V11.4ZM18.86,55.17a2.32,2.32,0,0,1,1.68-.8H58.83a2.3,2.3,0,0,1,1.73.79,2.81,2.81,0,0,1,0,3.68l0,0a2.32,2.32,0,0,1-1.72.8H20.58a2.31,2.31,0,0,1-1.73-.79,2.81,2.81,0,0,1,0-3.7Zm0-17.32a2.31,2.31,0,0,1,1.72-.79H43.21a2.33,2.33,0,0,1,1.71.76l0,0a2.82,2.82,0,0,1,0,3.71,2.32,2.32,0,0,1-1.72.8H20.58a2.31,2.31,0,0,1-1.73-.79,2.82,2.82,0,0,1,0-3.71ZM46.74,77H20.61a2.32,2.32,0,0,1-1.74-.79,2.79,2.79,0,0,1-.68-1.86,2.73,2.73,0,0,1,.69-1.84,2.34,2.34,0,0,1,1.73-.8H50.52A36,36,0,0,0,46.74,77ZM18.88,89.79a2.34,2.34,0,0,1,1.73-.8H42.33a35.38,35.38,0,0,0-.52,5.29H20.61a2.32,2.32,0,0,1-1.74-.79,2.78,2.78,0,0,1-.68-1.85,2.74,2.74,0,0,1,.69-1.85Zm75-10.54a22.56,22.56,0,1,0,6.62,16,22.47,22.47,0,0,0-6.62-16Z"
                        />
                    </svg>
                </div>
                <Typography variant="h2" gutterBottom>
                    You're all finished!
                </Typography>
                <Typography variant="h6" align="center">
                    Thank you for using the Timesheet Automation Tool! <br />
                    Successfully {submitted ? "submitted" : "uploaded"}{" "}
                    <span
                        style={{
                            color: theme.palette.success.main,
                        }}
                    >
                        {timeLogs.length}
                    </span>{" "}
                    time logs, totalling{" "}
                    <span
                        style={{
                            color: theme.palette.success.main,
                        }}
                    >
                        {decimalHourToString(getTimeLogTotalHours(timeLogs))}
                    </span>{" "}
                    hours.
                </Typography>
                {submitted && (
                    <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        sx={{ marginTop: 3 }}
                    >
                        <a
                            href={
                                "https://people.zoho.eu/principalhr/zp#timetracker/mydata/timesheets/edittimesheet-formId:26140000000014971/recordId:" +
                                timesheetId
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            view timesheet in Zoho People
                        </a>
                    </Button>
                )}
            </div>
            <Button
                sx={{ marginLeft: "auto" }}
                onClick={resetHandler}
                variant="outlined"
            >
                Reset
            </Button>
        </div>
    );
}
